/**
 * Season Configuration Computed Values
 *
 * Functions that derive values from the core season configuration.
 * These functions provide dynamic, time-aware data based on the current date.
 */

import type { ApplicationFeePhase, ComputedStatus, DateRange } from './types';
import { SEASON_CONFIG } from './core';

/**
 * Get the current application fee phase based on today's date
 *
 * @returns The current fee phase with deadlines and pricing
 */
export function getCurrentFeePhase(): ApplicationFeePhase {
  const now = new Date();

  for (const phase of SEASON_CONFIG.application.deadlines.phases) {
    if (now <= new Date(phase.deadline)) {
      return phase;
    }
  }

  // Return last phase if all deadlines have passed
  return SEASON_CONFIG.application.deadlines.phases[
    SEASON_CONFIG.application.deadlines.phases.length - 1
  ];
}

/**
 * Get the current application fees based on today's date
 *
 * @returns Current student and fellowship application fees
 */
export function getCurrentFees() {
  return getCurrentFeePhase().fees;
}

/**
 * Check if applications are currently open
 *
 * @returns true if applications are open, false otherwise
 */
export function isApplicationOpen(): boolean {
  return SEASON_CONFIG.application.status.isOpen;
}

/**
 * Get the number of days until the next application deadline
 *
 * @returns Number of days until next deadline, or null if past all deadlines
 */
export function getDaysUntilNextDeadline(): number | null {
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Normalize to start of day

  for (const phase of SEASON_CONFIG.application.deadlines.phases) {
    const deadline = new Date(phase.deadline);
    deadline.setHours(0, 0, 0, 0);

    if (now <= deadline) {
      const diffTime = deadline.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
  }

  return null;
}

/**
 * Get computed status information about the current application period
 *
 * @returns Computed status including open state, current phase, and days until deadline
 */
export function getComputedStatus(): ComputedStatus {
  return {
    isApplicationOpen: isApplicationOpen(),
    currentFeePhase: getCurrentFeePhase(),
    daysUntilNextDeadline: getDaysUntilNextDeadline()
  };
}

/**
 * Get the date range for the full festival session
 *
 * @returns Start and end dates as Date objects
 */
export function getFullSessionDateRange(): DateRange {
  const { arrival, departure } = SEASON_CONFIG.festival.season.fullSession;

  return {
    start: new Date(arrival.date),
    end: new Date(departure.date)
  };
}

/**
 * Get the date range for a specific session
 *
 * @param sessionId - The session ID (1 or 2)
 * @returns Start and end dates as Date objects, or null if session not found
 */
export function getSessionDateRange(sessionId: number): DateRange | null {
  const session = SEASON_CONFIG.festival.season.sessions.find(s => s.id === sessionId);

  if (!session) {
    return null;
  }

  // Parse the date range string (e.g., "June 29 – July 19, 2027")
  const [startStr, endStr] = session.dates.split('–').map(s => s.trim());
  const year = SEASON_CONFIG.festival.season.year;

  // Construct full date strings
  const startDate = new Date(`${startStr}, ${year}`);
  const endDate = new Date(endStr); // Already includes year

  return {
    start: startDate,
    end: endDate
  };
}

/**
 * Calculate total cost for a student application
 *
 * @param programType - "fullSession" or "singleSession"
 * @returns Total cost including tuition and fees
 */
export function calculateStudentTotalCost(
  programType: 'fullSession' | 'singleSession'
): number {
  const tuition = SEASON_CONFIG.festival.tuition[programType].student as number;
  const { registration, comprehensive, damage } = SEASON_CONFIG.festival.tuition.additionalFees;

  return (
    tuition +
    (registration.student as number) +
    (comprehensive.student as number) +
    (damage.student as number)
  );
}

/**
 * Get the season year
 *
 * @returns The current season year
 */
export function getSeasonYear(): number {
  return SEASON_CONFIG.festival.season.year;
}

/**
 * Check if a given date falls within the festival period
 *
 * @param date - The date to check
 * @returns true if date is within the festival period
 */
export function isDateDuringFestival(date: Date): boolean {
  const { start, end } = getFullSessionDateRange();
  return date >= start && date <= end;
}

/**
 * Get the next upcoming concert (opening or closing)
 *
 * @returns The next concert info, or null if both have passed
 */
export function getNextConcert() {
  const now = new Date();
  const { opening, closing } = SEASON_CONFIG.festival.season.concerts;

  const openingDate = new Date(opening.date);
  const closingDate = new Date(closing.date);

  if (now < openingDate) {
    return opening;
  }

  if (now < closingDate) {
    return closing;
  }

  return null;
}

/**
 * Get the application opening date formatted for display
 *
 * @returns Object with date string and label
 */
export function getApplicationDeadline() {
  const startDate = new Date(`${SEASON_CONFIG.application.deadlines.start}T00:00:00Z`);

  // Format as "Month Day, Year" (e.g., "November 15, 2026").
  // Pinned to UTC so the ISO date is not shifted back a day in western timezones.
  const formatted = startDate.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return {
    date: formatted,
    label: "APPLICATION OPENS"
  };
}

/**
 * Get the ordinal number of the current season
 *
 * @returns The season number (e.g., 16 for the 16th season)
 */
export function getSeasonNumber(): number {
  return SEASON_CONFIG.festival.season.number;
}

/**
 * Get the current year for copyright notices
 *
 * @returns The current year
 */
export function getCopyrightYear(): number {
  return new Date().getFullYear();
}